import _ from 'lodash';
import React, { Component } from 'react';
// TODO: Migrate module so that you don’t import from the corp project in a global module
/* eslint-disable restrict-imports/restrict-imports */
import SelectScalien from 'corp/components/UI/SelectScalien';
import { Label, Button } from 'design-system/Form';
import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import SelectPermissionRole from 'corp/components/UI/SelectPermissionRole';
import { SelectOption, renderSelectedObjects } from 'corp/components/UI/Select/helpers';
import { fetchUsersWithRole } from 'react-scale/containers/corp/corp-permissions/helpers';

interface IGiveCorpPermissionRoleProps {
  showRole?: boolean;
  corpPermissionRoleId?: string;
}

interface IGiveCorpPermissionRoleState {
  corpPermissionRole?: SelectOption;
  usersWithRole: SelectOption[]; // only show options _not_ in this list
  selectedUsers?: SelectOption[];
  loading: boolean;
  message?: string;
  succeeded?: boolean;
}

export default class GiveCorpPermissionRole extends Component<
  IGiveCorpPermissionRoleProps,
  IGiveCorpPermissionRoleState
> {
  initialState = {
    corpPermissionRole: this.props.corpPermissionRoleId
      ? { label: '', value: this.props.corpPermissionRoleId }
      : undefined,
    usersWithRole: [],
    selectedUsers: undefined,
    loading: false,
    message: undefined,
    succeeded: undefined,
  };

  constructor(props: IGiveCorpPermissionRoleProps, context: $FIXME) {
    super(props, context);
    this.state = this.initialState;
    this.onChangeCorpPermissionRole = this.onChangeCorpPermissionRole.bind(this);
    this.onSelectUser = this.onSelectUser.bind(this);
    this.onReset = this.onReset.bind(this);
    this.giveCorpPermissionRole = this.giveCorpPermissionRole.bind(this);
  }

  static defaultProps = {
    showRole: true,
  };

  async onChangeCorpPermissionRole(corpPermissionRole?: SelectOption): Promise<void> {
    this.setState({ loading: true });
    const usersWithRole = await fetchUsersWithRole(corpPermissionRole);
    this.setState({
      corpPermissionRole,
      usersWithRole,
      loading: false,
      message: undefined,
      succeeded: undefined,
    });
  }

  onSelectUser(selectedUser?: SelectOption): void {
    if (!selectedUser) return;
    const selectedUsers = this.state.selectedUsers
      ? _.uniqBy([...this.state.selectedUsers, selectedUser], 'value')
      : [selectedUser];
    this.setState({ selectedUsers, message: undefined, succeeded: undefined });
  }

  onReset(): void {
    this.setState({ ...this.initialState });
  }

  async giveCorpPermissionRole(event: React.MouseEvent<HTMLElement>): Promise<void> {
    event.preventDefault();

    const isDisabled =
      this.state.loading || !this.state.corpPermissionRole || !this.state.selectedUsers;
    if (isDisabled) return;
    this.setState({ loading: true, message: undefined, succeeded: undefined });

    try {
      const corpPermissionRoleId = this.state.corpPermissionRole!.value;
      const userIds = _.uniq(this.state.selectedUsers!.map(option => option.value));

      const { success, retMessage } = await apiFetch(null, `corp-api/give_corp_permission_role`, {
        method: 'POST',
        body: {
          corpPermissionRoleId,
          userIds,
        },
      });

      this.setState({ succeeded: success, message: retMessage });
    } catch (error) {
      defaultApiFetchErrorHandler(error, { handleHttp: true });
    } finally {
      this.setState({ loading: false });
    }
  }

  renderGiveCorpPermissionRole() {
    const corpRoleSummary = this.state.corpPermissionRole ? (
      <div>
        {`Found ${this.state.usersWithRole.length} corp users with role `}
        <a href={`lookup/${this.state.corpPermissionRole.value}`}>
          {this.state.corpPermissionRole.label}
        </a>
      </div>
    ) : (
      ''
    );

    return (
      <div className="container">
        {this.props.showRole && (
          <>
            <div className="text">
              This tool handles giving an existing corp permission role to a list of corp users. To
              use it, you need to be a privileged user (i.e. have <b>onsite</b> access role) for all
              projects/customers included in the permission role.
            </div>
            <div>
              <Label>Corp Permission Role</Label>
              <SelectPermissionRole
                value={this.state.corpPermissionRole}
                disabled={this.state.loading}
                onChange={this.onChangeCorpPermissionRole}
              />
              {corpRoleSummary}
            </div>
          </>
        )}
        <div>
          <Label>Corp Users</Label>
          <SelectScalien
            placeholder="Select corp users to receive this role"
            disabled={this.state.loading || !this.state.corpPermissionRole}
            onChange={this.onSelectUser}
            filterOption={(option: SelectOption) =>
              !_.find(this.state.usersWithRole, x => x.value === option.value)
            }
          />
          {renderSelectedObjects(this.state.selectedUsers)}
        </div>
        <div className="buttons">
          <Button
            primary
            type="button"
            disabled={
              this.state.loading || !this.state.corpPermissionRole || !this.state.selectedUsers
            }
            onClick={this.giveCorpPermissionRole}
          >
            {this.state.loading ? 'Loading...' : 'Give Corp Permission Role'}
          </Button>
          &emsp;&emsp;
          <Button type="button" disabled={this.state.loading} onClick={this.onReset}>
            {this.state.loading ? 'Loading...' : 'Reset Form'}
          </Button>
        </div>
        {this.state.message && (
          <div className={this.state.succeeded ? 'successMessage' : 'errorMessage'}>
            {this.state.message}
          </div>
        )}
        <style jsx>{`
          .container {
            max-width: 1000px;
            min-height: 250px;
            padding-bottom: 20px;
            margin: 20px 20px;
            font-weight: normal;
          }
          .selector {
            margin: 20px 0;
          }
          .selector .title {
            font-size: 20px;
            margin: 10px 0;
          }
          h2.subtitle {
            font-size: 16px;
          }
          .buttons {
            margin-top: 12px;
            text-align: center;
          }
          .successMessage {
            margin-top: 12px;
            border: 1px solid rgba(0, 255, 0, 0.3);
            border-radius: 4px;
            background-color: rgba(0, 255, 0, 0.03);
            padding: 16px;
            color: green;
          }
          .errorMessage {
            margin-top: 12px;
            border: 1px solid rgba(255, 0, 0, 0.3);
            border-radius: 4px;
            background-color: rgba(255, 0, 0, 0.03);
            padding: 16px;
            color: brown;
          }
        `}</style>
      </div>
    );
  }

  render() {
    return <div id="body">{this.renderGiveCorpPermissionRole()}</div>;
  }
}
